import { useEffect, useState } from 'react';
import azureDevOpsService from './azureDevOpsService';
import { getCoverage } from './knownFailuresService';

const BASE = '/api/suite-verifications';

function authHeaders() {
  const pat = azureDevOpsService.getPat();
  return pat ? { 'X-Azure-Devops-Pat': pat } : {};
}

export function suiteVerificationKey(pipelineId, buildId, suiteName) {
  return `${pipelineId}_${String(buildId).replace(/^build-/, '')}_${suiteName}`;
}

/**
 * Vérification d'une suite. `name` null = n'importe quelle suite de ce build :
 * utile pour ReadyAPI (un build = une suite), dont le nom affiché diffère entre
 * la Vue globale et les pages équipe.
 */
export function findVerification(verifications, suite, knownFailures) {
  return findManualVerification(verifications, suite) || autoVerification(suite, getCoverage(knownFailures, suite));
}

function findManualVerification(verifications, { pipelineId, buildId, name }) {
  if (!verifications || !buildId) return null;
  if (name) return verifications[suiteVerificationKey(pipelineId, buildId, name)] || null;
  const prefix = suiteVerificationKey(pipelineId, buildId, '');
  const key = Object.keys(verifications).find(k => k.startsWith(prefix));
  return key ? verifications[key] : null;
}

/**
 * Un run n'a rien à revoir quand il n'a aucun échec, ou quand chacun de ses
 * échecs a déjà un bug lié (`coverage`, cf. knownFailuresService) : il est alors
 * considéré vérifié d'office, daté du run lui-même. Calculé à l'affichage, jamais
 * enregistré — une vérification manuelle existante reste prioritaire.
 * `reason` : 'no-failure' | 'known-bugs'.
 */
export function autoVerification({ failed, totalTests, executionDate }, coverage = null) {
  if (!(totalTests > 0)) return null;
  const base = { auto: true, verifiedBy: null, verifiedAt: executionDate || null };
  if (failed === 0) return { ...base, reason: 'no-failure' };
  if (coverage?.allCovered) return { ...base, reason: 'known-bugs', failedWithBug: coverage.withBug };
  return null;
}

/** { total, verified: [...vérifications trouvées], allVerified } pour une liste de suites. */
export function summarizeVerifications(verifications, suites, knownFailures) {
  const verifiable = (suites || []).filter(s => s.buildId);
  const verified = verifiable.map(s => findVerification(verifications, s, knownFailures)).filter(Boolean);
  return { total: verifiable.length, verified, allVerified: verifiable.length > 0 && verified.length === verifiable.length };
}

// Petit store partagé : une page affiche une carte par suite + un bandeau de
// synthèse, qui doivent tous réagir quand une carte est (dé)vérifiée. Un seul
// GET alimente tout le monde ; les écritures mettent à jour le store localement.
let data = null;
let loadPromise = null;
let loadedAt = 0;
const listeners = new Set();

function notify() {
  listeners.forEach(fn => fn(data));
}

// Recharge si les données ont plus de quelques secondes : d'autres personnes ont
// pu vérifier entre-temps, mais toutes les cartes d'une même page (montées
// ensemble) partagent le même appel.
const FRESH_MS = 5000;

function load() {
  if (!loadPromise || Date.now() - loadedAt > FRESH_MS) {
    loadedAt = Date.now();
    loadPromise = fetch(BASE, { headers: authHeaders() })
      .then(res => {
        if (!res.ok) throw new Error('suite-verifications fetch failed');
        return res.json();
      })
      .then(json => {
        data = json;
        notify();
        return data;
      })
      .catch(err => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
}

// Le proxy CRA / Express renvoient du HTML sur 404 : on ne peut pas s'y fier
// pour un message, d'où le diagnostic explicite.
async function errorFrom(res, fallback) {
  const payload = await res.json().catch(() => null);
  if (payload?.error) return new Error(payload.error);
  if (res.status === 404) {
    return new Error(`${fallback} : route absente du backend (HTTP 404). Le serveur du port 3001 a-t-il été redémarré ?`);
  }
  return new Error(`${fallback} (HTTP ${res.status})`);
}

/** Map clé -> vérification, ou null tant que le premier chargement n'est pas fini. */
export function useSuiteVerifications() {
  const [snapshot, setSnapshot] = useState(data);
  useEffect(() => {
    listeners.add(setSnapshot);
    load().catch(() => setSnapshot(prev => prev || {}));
    return () => { listeners.delete(setSnapshot); };
  }, []);
  return snapshot;
}

const suiteVerificationService = {
  async verify({ pipelineId, buildId, suiteName, version }) {
    const res = await fetch(BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ pipelineId, buildId, suiteName, version })
    });
    if (!res.ok) throw await errorFrom(res, 'Impossible de marquer la suite comme analysée');
    const { key, verification } = await res.json();
    data = { ...(data || {}), [key]: verification };
    notify();
    return verification;
  },

  async unverify({ pipelineId, buildId, suiteName }) {
    const res = await fetch(BASE, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ pipelineId, buildId, suiteName })
    });
    if (!res.ok) throw await errorFrom(res, 'Impossible d\'annuler l\'analyse');
    const next = { ...(data || {}) };
    delete next[suiteVerificationKey(pipelineId, buildId, suiteName)];
    data = next;
    notify();
  }
};

export default suiteVerificationService;
